import { useEffect, useState } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import InputDrawer from "./InputDrawer";
import SectionsContainer from "./SectionsContainer";
import InputModal from "./InputModal";
import SectionModal from "./SectionModal";
import NotesSection from "./NotesSection";
import DocumentSection from "./DocumentSection";
import TaskSection from "./TaskSection";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  addInput,
  addNewSection,
  deleteInput,
  deleteSection,
  deleteTemplate,
  deployTemplate,
  getTemplateDetails,
  updateInput,
  updateSection,
  updateTemplateDetails,
} from "../../utils/api/customTemplatesApi";
import { pushAlert } from "../../utils/alertSlice";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../utils/loaderSlice";
import { useNavigate } from "react-router-dom";
import DeployedDialog from "./DeployedDialog";
import Button from "../UniversalComponents/DefaultButton";
import ConfirmationDialogMulti from "../UniversalComponents/ConfirmationDialogMulti";
import { INITIAL_INPUTS } from "./extras/Constants";
import { checkAccessPermission, getIsOwner } from "../../utils/utilityFunctions/localServices";
import InfoButton from "../UniversalComponents/InfoButton";
import MotionTyping from "../UniversalComponents/MotionTyping";
import { ReactComponent as LeftBlueArrow } from "../../assets/images/icons/LeftBlueArrow1.svg";
import { getTitleCase } from "../../utils/utilityFunctions/stringUtility";
import { ReactComponent as CustomizationLayer } from "../../assets/images/CustomizationLayer.svg";
import { ReactComponent as Deletecustomization } from "../../assets/images/delete-customization.svg";
import { ReactComponent as Sendcustomization } from "../../assets/images/send-customization.svg";
import { ReactComponent as MoveDraftcustomization } from "../../assets/images/MoveDraft-customization.svg";
import Skeleton from "../UniversalComponents/CustomTempModuleSkeleton";

const INITIAL_DRAWER_PERMISSIONS = [
  {
    label: "Create",
    value: true,
    component: ({
      sections,
      onEditSection,
      onDeleteSection,
      onEditInput,
      onDeleteInput,
      setSectionNameModal,
      disabled,
    }) => (
      <SectionsContainer
        sections={sections}
        onEditSection={onEditSection}
        onDeleteSection={onDeleteSection}
        onEditInput={onEditInput}
        onDeleteInput={onDeleteInput}
        setSectionNameModal={setSectionNameModal}
        disabled={disabled}
      />
    ),
    showInInputSection: false,
  },
  {
    label: "Notes",
    value: false,
    component: () => <NotesSection />,
    showInInputSection: true,
  },
  {
    label: "Documents",
    value: false,
    component: () => <DocumentSection />,
    showInInputSection: true,
  },
  {
    label: "Task",
    value: false,
    component: () => <TaskSection />,
    showInInputSection: true,
  },
];

// ================================
// Main App
// ================================
export const TemplateBuilder = ({ templateId }) => {
  const [sections, setSections] = useState([]);
  const [customApiData, setCustomApiData] = useState({});
  const [sectionNameModal, setSectionNameModal] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [showDeployedDialog, setShowDeployedDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState({ show: false, data: { type: null, id: null } });
  const [showWarningConvertToSingle, setShowWarningConvertToSingle] = useState({
    show: false,
    data: { type: null, id: null },
  });

  const [showDeleteTemplateDialog, setShowDeleteTemplateDialog] = useState(false);

  const [hasPermission, setHasPermission] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [dragCapture, setDragCapture] = useState({ isOpen: false, data: {} });

  const [inputPermissionList, setInputPermissionList] = useState(INITIAL_DRAWER_PERMISSIONS || []);
  const [activeTab, setActiveTab] = useState(INITIAL_DRAWER_PERMISSIONS[0]);
  const [loading, setLoading] = useState(false);

  const handleDragEnd = (result) => {
    const { source, destination, draggableId, type } = result;

    if (!destination) return;

    // Dragging sections
    if (type === "SECTION") {
      const newSections = Array.from(sections);
      const [moved] = newSections.splice(source.index, 1);
      newSections.splice(destination.index, 0, moved);
      setSections(newSections);
      return;
    }

    // Dragging inputs
    if (type === "INPUT") {
      if (destination.droppableId === "INPUT_DRAWER") return;

      // From drawer to section
      if (source.droppableId === "INPUT_DRAWER") {
        const inputInfo = INITIAL_INPUTS.find((i) => i.inputType === draggableId);
        const sectionId = result?.destination?.droppableId;
        const sectionInfo = sections.find((s) => s.sectionId === sectionId);

        // "result" consist the data about whom to drop and where to drop.(Ids of them)

        const data = {
          inputId: null,
          inputValue: null,
          inputDescription: null,
          inputType: inputInfo?.inputType,
          inputName: inputInfo?.inputName,
          // baseInputType: inputInfo?.baseInputType,
          sectionId: sectionInfo?.sectionId,
          sectionName: sectionInfo?.sectionName,
          // sectionRank: destination.index,
        };

        setDragCapture({ isOpen: true, data });
        // const targetSectionIndex = sections.findIndex(
        //   (s) => s.id === destination.droppableId
        // );
        // const newSections = Array.from(sections);
        // const inputTemplate = INITIAL_INPUTS.find((i) => i.id === draggableId);
        // const newInput = {
        //   ...inputTemplate,
        //   uniqueId: `${inputTemplate.id}-${Date.now()}`,
        // };
        // newSections[targetSectionIndex].inputs.splice(
        //   destination.index,
        //   0,
        //   newInput
        // );
        // setSections(newSections);
        return;
      }

      // Move/reorder inside or across sections
      // const sourceSectionIndex = sections.findIndex((s) => s.sectionId === source.droppableId);
      // const destSectionIndex = sections.findIndex((s) => s.sectionId === destination.droppableId);

      if (source.droppableId === destination.droppableId) {
        dispatch(pushAlert({ visible: true, message: "Cannot move input to same section", type: "info" }));
      } else {
        handleSwapSectionsOfInput({
          newSectionId: destination.droppableId,
          inputId: draggableId,
        });
      }

      // const newSections = Array.from(sections);
      // const [moved] = newSections[sourceSectionIndex].inputs.splice(source.index, 1);
      // newSections[destSectionIndex].inputs.splice(destination.index, 0, moved);
      // setSections(newSections);
    }
  };

  const handleSwapSectionsOfInput = async ({ newSectionId, inputId }) => {
    try {
      const response = await updateInput({ body: { section_id: newSectionId, id: inputId } });
      if (response?.status) {
        getSectionsAndInputByTemplateId();
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message || "Something went wrong", type: "danger" }));
      }
      setDragCapture({
        isOpen: false,
        data: {},
      });
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const handleAddInput = async (formInputData) => {
    try {
      const inputInfo = INITIAL_INPUTS.find((i) => i.inputType === formInputData?.type);
      let body = {
        field_label: formInputData?.name,
        description: formInputData?.description,
      };

      if (formInputData?.inputId) {
        body.id = formInputData?.inputId;
      } else {
        body.section_id = formInputData?.sectionId;
        body.field_type = inputInfo?.baseInputType;
      }

      // ---------- FOR CHOICE/MULTISELECT -----------------------------------------
      if (formInputData?.type === "choice" && formInputData?.allowMultiple) {
        body = { ...body, field_type: "multiselect" };
      } else if (formInputData?.type === "choice" && !formInputData?.allowMultiple) {
        body = { ...body, field_type: "dropdown" };
      }
      // ---------- FOR CHOICE/MULTISELECT -----------------------------------------

      // ---------- FOR PERSON/MULTIPERSON -----------------------------------------
      if (formInputData?.type === "person" && formInputData?.multiPerson) {
        body = { ...body, field_type: "multi_person" };
      } else if (formInputData?.type === "person" && !formInputData?.multiPerson) {
        body = { ...body, field_type: "person" };
      }
      // ---------- FOR PERSON/MULTIPERSON -----------------------------------------

      let meta = {
        dateTimeFormat: {
          timeDisplay: null, // Date & Time , Date
        },
        numberFormat: {
          decimalPlaces: formInputData?.decimalPlaces,
          negativeFormat: formInputData?.negativeNumber,
          // selectedOption: "Currency",
          separators: formInputData?.separatorInput,
          symbol: formInputData?.symbol,
        },
        textFormat: {
          textSelectedOption: null,
        },
        absoluteType: formInputData?.type,
      };

      if (formInputData?.type === "email") {
        meta = { ...meta, textFormat: { textSelectedOption: "Email" } };
      } else if (formInputData?.type === "shortText") {
        meta = { ...meta, textFormat: { textSelectedOption: "Short Text" } };
      } else if (formInputData?.type === "longText") {
        meta = { ...meta, textFormat: { textSelectedOption: "Long Text" } };
      } else if (formInputData?.type === "hyperlink") {
        meta = { ...meta, textFormat: { textSelectedOption: "Hyperlink" } };
      } else if (formInputData?.type === "url") {
        meta = { ...meta, textFormat: { textSelectedOption: "URL" } };
      } else if (formInputData?.type === "datetime") {
        meta = { ...meta, dateTimeFormat: { timeDisplay: "Date & Time" } };
      } else if (formInputData?.type === "date") {
        meta = { ...meta, dateTimeFormat: { timeDisplay: "Date" } };
      } else if (formInputData?.type === "choice") {
        body.dropdown_options = formInputData?.options?.map((option) => {
          return {
            color: option?.color_code,
            value: option?.value,
          };
        });
      }

      let response;
      if (formInputData?.inputId) {
        // Remove field_type since when updating, we dont need to send fieldType
        delete body.field_type;

        response = await updateInput({ body: { ...body, meta } });
      } else {
        response = await addInput({ body: { ...body, meta } });
      }

      if (response?.status) {
        getSectionsAndInputByTemplateId();
        setDragCapture({ isOpen: false, data: {} });
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    }
  };

  const handleDeleteSection = async (sectionId) => {
    // setSections(sections.filter((s) => s.sectionId !== sectionId));

    try {
      const response = await deleteSection({ params: { section_id: sectionId } });
      if (response?.status) {
        getSectionsAndInputByTemplateId();
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message || "Something went wrong", type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const handleDeleteInput = async (inputId) => {
    try {
      const response = await deleteInput({ params: { field_id: inputId } });
      if (response?.status) {
        getSectionsAndInputByTemplateId();
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message || "Something went wrong", type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const handleAddSection = async ({ inputs }) => {
    try {
      if (!inputs?.name?.trim()) {
        setSectionNameModal(false);
        dispatch(pushAlert({ visible: true, message: "Please enter section name", type: "danger" }));
        return;
      }
      const response = await addNewSection({
        body: {
          section_name: inputs?.name,
          template_id: templateId,
        },
      });

      if (response?.status) {
        getSectionsAndInputByTemplateId();
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }

      setSectionNameModal(false);
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const handleEditSection = async ({ event, sectionId, sectionName, sectionDescription }) => {
    try {
      if (!sectionName?.trim()) {
        setSectionNameModal(false);
        dispatch(pushAlert({ visible: true, message: "Please enter section name", type: "danger" }));
        return;
      }

      const response = await updateSection({
        body: {
          section_name: sectionName,
          id: sectionId,
          template_id: templateId,
        },
      });

      if (response?.status) {
        getSectionsAndInputByTemplateId();
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }

      setSectionNameModal(false);
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const handleEditInputDialog = (input) => {
    const inputTypeInfo = INITIAL_INPUTS.find((i) => i.inputType === input.inputType);

    setDragCapture({
      isOpen: true,
      data: {
        inputId: input.inputId,
        inputValue: input.inputValue,
        inputDescription: input.inputDescription,
        inputType: input?.inputMeta?.absoluteType,
        inputName: inputTypeInfo?.inputName,
        sectionId: input.sectionId,
        sectionName: input.sectionName,
        inputMeta: input.inputMeta,
        inputUniqueName: input.inputUniqueName,
      },
    });
  };

  const convertApiResponseToCustom = ({ apiData }) => {
    let data = apiData?.sections?.map((sectionInfo) => {
      return {
        sectionId: sectionInfo?.section_id,
        sectionName: sectionInfo?.section_name,
        sectionRank: sectionInfo?.section_rank,
        sectionIsEditable: sectionInfo?.is_editable,
        sectionInfo: sectionInfo?.info,
        inputs: sectionInfo?.fields?.map((fieldInfo) => {
          return {
            inputId: fieldInfo?.field_id,
            inputValue: fieldInfo?.field_label,
            inputDescription: fieldInfo?.description,
            inputType: fieldInfo?.field_type,
            inputRank: fieldInfo?.field_rank,
            inputMeta: fieldInfo?.meta,
            inputUniqueName: fieldInfo?.field_name,
            inputIsEditable: fieldInfo?.is_editable,
            inputInfo: fieldInfo?.info,
          };
        }),
      };
    });

    data = {
      sections: data,
      isNotes: Boolean(apiData?.is_notes),
      isDocuments: Boolean(apiData?.is_documents),
      isTasks: Boolean(apiData?.is_task),
      isActive: Boolean(apiData?.is_active),
      isDeployed: Boolean(apiData?.is_deployed),
      isMultiRecord: Boolean(apiData?.is_multi_records),
      templateId: apiData?.template_id,
      templateName: apiData?.template_name,
      onlyCompanyLevel: apiData?.only_company_level,
      color: apiData?.color || { from: "#086E74", to: "#0FCFDA" },
      icon: apiData?.icon || "bi bi-border-style",
    };

    return data;
  };

  const getSectionsAndInputByTemplateId = async () => {
    try {
      setIsApiLoading(true);
      const response = await getTemplateDetails({ params: { template_id: templateId } });
      setLoading(response?.status);
      if (response?.status) {
        const data = convertApiResponseToCustom({ apiData: response?.data });
        setInputPermissionList([
          {
            label: "Create",
            value: true,
            component: ({ sections, onEditSection, onDeleteSection, onEditInput, onDeleteInput }) => (
              <SectionsContainer
                sections={sections}
                onEditSection={onEditSection}
                onDeleteSection={onDeleteSection}
                onEditInput={onEditInput}
                onDeleteInput={onDeleteInput}
                setSectionNameModal={setSectionNameModal}
                disabled={data?.isDeployed}
              />
            ),
            showInInputSection: false,
          },
          {
            label: "Notes",
            value: data?.isNotes,
            component: () => <NotesSection />,
            showInInputSection: true,
          },
          {
            label: "Documents",
            value: data?.isDocuments,
            component: () => <DocumentSection />,
            showInInputSection: true,
          },
          {
            label: "Task",
            value: data?.isTasks,
            component: () => <TaskSection />,
            showInInputSection: true,
          },
        ]);

        setCustomApiData(data);
        setSections(data?.sections);
      } else {
        dispatch(
          pushAlert({
            visible: true,
            message: response?.message,
            type: "danger",
          })
        );
      }
    } catch (error) {
      dispatch(
        pushAlert({
          visible: true,
          message: error?.message || "Something went wrong",
          type: "danger",
        })
      );
    } finally {
      setIsApiLoading(false);
    }
  };

  const handleUpdateTemplatePermission = async (data, result) => {
    try {
      let body = {
        id: templateId,
      };
      if (data?.label === "Notes") {
        body.is_notes = result;
      } else if (data?.label === "Documents") {
        body.is_documents = result;
      } else if (data?.label === "Task") {
        body.is_task = result;
      } else if (data?.label === "multipleRecords") {
        body.is_multi_records = result;
      } else if (data?.label === "onlyCompanyLevel") {
        body.only_company_level = result;
      }

      const response = await updateTemplateDetails({ body });
      if (response?.status) {
        getSectionsAndInputByTemplateId();
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const handleDeploy = async () => {
    try {
      const emptySectionsList = [];
      customApiData?.sections?.forEach((item) => {
        if (!item?.inputs?.length) {
          emptySectionsList.push(item);
        }
      });

      if (emptySectionsList?.length) {
        dispatch(
          pushAlert({
            visible: true,
            message: "Please add fields in all sections",
            type: "danger",
          })
        );
        return;
      }

      const response = await deployTemplate({
        body: { id: templateId, is_deployed: !customApiData?.isDeployed, scope: "customer" },
      });
      if (response?.status) {
        getSectionsAndInputByTemplateId();
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
        setShowDeployedDialog(true);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const handleDelete = async () => {
    try {
      if (showDeleteDialog?.type === "section") {
        handleDeleteSection(showDeleteDialog?.data);
      } else {
        handleDeleteInput(showDeleteDialog?.data);
      }
      setShowDeleteDialog({ show: false, type: "", data: {} });
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  const hanldeDeleteCustomTemplate = async () => {
    try {
      const response = await deleteTemplate({ params: { template_id: templateId } });
      if (response.success || response?.status) {
        dispatch(pushAlert({ visible: true, message: response?.message || "Successfull", type: "success" }));
        navigate("/setting/customization", { replace: true });
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message || "Something went wrong", type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Something went wrong", type: "danger" }));
    }
  };

  useEffect(() => {
    getSectionsAndInputByTemplateId();
  }, []);

  useEffect(() => {
    if (isApiLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [isApiLoading]);

  console.log(hasPermission, "hasPermission");

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex flex-col h-full gap-3">
        {loading ? (
          <div className="flex justify-between items-center">
            <div className="flex gap-3 items-center">
              <button
                onClick={() => {
                  navigate("/setting/customization/workspace", { replace: true });
                }}
                className="text-primary py-1 rounded-md flex items-center"
              >
                <LeftBlueArrow className="fill-[#13131399] hover:fill-primary" />{" "}
                {/* <span className="px-1 font-600 text-base">{getTitleCase(customApiData?.templateName)}</span> */}
              </button>
              <div
                style={{
                  background: `linear-gradient(to right, ${customApiData?.color?.from || "#086E74"} 0%, ${customApiData?.color?.to || "#0FCFDA"} 100%)`,
                }}
                className="flex h-11 w-11 items-center justify-center rounded-xl text-white"
              >
                <i className={`${customApiData?.icon || "bi bi-border-style"} text-white text-2xl`}></i>
              </div>

              {/* task draft*/}
              <div className="flex flex-col gap-1 ">
                <span className="flex gap-3 items-center">
                  <span className="text-base font-semibold text-primary">
                    {getTitleCase(customApiData?.templateName)}
                  </span>
                  {customApiData?.isDeployed ? (
                    <span className="text-[#3DB22A] bg-[#3DB22A1A] text-sm py-1 px-4 rounded-md">Deployed</span>
                  ) : (
                    <span className="text-[#1A1A1A] bg-[#F2F2F2] text-sm py-1 px-4 rounded-md pl-4">Draft</span>
                  )}
                </span>
                <span>
                  {customApiData?.isDeployed && (
                    <MotionTyping
                      text="To make edits, please move this to the draft section."
                      stagger={0.05}
                      initialDelay={0.2}
                      repeatDelay={0.2}
                    />
                  )}
                </span>
              </div>
            </div>
            {/* header div right delete and Save and Deploy start */}
            <div className="flex gap-4 items-center justify-center">
              {!customApiData?.isDeployed && hasPermission && (
                <>
                  <button
                    onClick={() => setShowDeleteTemplateDialog(true)}
                    className="bg-[#FFFFFF] hover:bg-[#FFF5F5] border border-[#FFE2E2] flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer"
                  >
                    <span>
                      <Deletecustomization />
                    </span>
                    <span className="text-sm font-medium text-[#E7000B] flex justify-center items-center">Delete</span>
                  </button>
                  <span className="h-[24px] w-0.5 border-l border-[#E2E8F0]"></span>
                </>
              )}

              {customApiData?.isDeployed && hasPermission && (
                <button
                  onClick={handleDeploy}
                  className="bg-[#FFFFFF] hover:bg-[#F3F3F3] border border-[#E2E8F0] flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer"
                >
                  <span>
                    <MoveDraftcustomization />
                  </span>
                  <span className="text-sm font-medium text-[#131313CC] flex justify-center items-center">
                    Move to draft
                  </span>
                </button>
              )}
              {!customApiData?.isDeployed && hasPermission && (
                <button
                  onClick={handleDeploy}
                  className="bg-primary-70 hover:bg-primary text-white flex items-center px-4 py-2 gap-2 rounded-lg shadow-sm cursor-pointer"
                >
                  <span>
                    <Sendcustomization />
                  </span>
                  Save and Deploy
                </button>
              )}
            </div>

            {/* me working in this code */}
          </div>
        ) : (
          <div className="w-full h-14">
            <Skeleton variant="header" />
          </div>
        )}

        <div className={`flex min-h-0 grow gap-3 border-t w-full `}>
          {/* Left Side - Sections */}
          <div className="w-[20%] h-full overflow-auto ">
            <InputDrawer
              disabled={customApiData?.isDeployed}
              customApiData={customApiData}
              sections={sections}
              inputs={INITIAL_INPUTS}
              setSectionNameModal={setSectionNameModal}
              setInputPermissionList={setInputPermissionList}
              inputPermissionList={inputPermissionList}
              handleUpdateTemplatePermission={handleUpdateTemplatePermission}
            />
          </div>

          {/* Right Side - Input Drawer */}
          <div className="min-w-0 grow flex flex-col justify-between gap-y-2 h-full">
            <div className="flex justify-between items-center ">
              <div>
                {/* // ONLY SHOW THE TABS WHEN THE VALUE IS TRUE */}
                {inputPermissionList
                  ?.filter((item) => item.value)
                  .map((item, index) => (
                    <button
                      key={index}
                      className={` px-4 py-2 ${activeTab.label === item.label ? "border-b-[2px] border-[var(--primary-color)] text-primary" : "border-b-[2px] border-b-white"}`}
                      onClick={() => setActiveTab(item)}
                    >
                      {item?.label}
                    </button>
                  ))}
              </div>
            </div>

            <div className="min-h-0 grow overflow-auto">
              {inputPermissionList
                ?.filter((item) => item.value && activeTab?.label === item.label)
                .map((item, index) => {
                  return (
                    <>
                      {item.component({
                        sections: sections,
                        onEditSection: (section) => {
                          setSectionNameModal(section);
                        },
                        onDeleteSection: (sectionInfo) =>
                          setShowDeleteDialog({ show: true, type: "section", data: sectionInfo }),
                        onEditInput: (sectionId, sectionName, input) => {
                          handleEditInputDialog({ sectionId, sectionName, ...input });
                        },
                        onDeleteInput: (input) => setShowDeleteDialog({ show: true, type: "input", data: input }),
                        setSectionNameModal: setSectionNameModal,
                        disabled: customApiData?.isDeployed,
                      })}
                    </>
                  );
                })}
            </div>
          </div>
        </div>

        {sectionNameModal && (
          <SectionModal
            onClose={() => setSectionNameModal(false)}
            open={sectionNameModal}
            sectionInfo={sectionNameModal}
            handleSubmit={handleAddSection}
            handleEditSection={handleEditSection}
          />
        )}

        {dragCapture?.isOpen && (
          <InputModal
            // handleEditInput={handleEditInput}
            handleAddInput={handleAddInput}
            dragCapture={dragCapture}
            setDragCapture={setDragCapture}
          />
        )}

        {showDeployedDialog && (
          <DeployedDialog
            open={showDeployedDialog}
            onClose={() => setShowDeployedDialog(false)}
            isDeployed={customApiData?.isDeployed}
          />
        )}

        {showDeleteDialog?.show && (
          <ConfirmationDialogMulti
            open={showDeleteDialog?.show}
            onClose={() => {
              setShowDeleteDialog({ show: false, type: null, data: null });
            }}
            title="Delete ?"
            message="Are you sure you want to delete this ? This action cannot be undone."
            onConfirm={() => {
              handleDelete();
            }}
            onCancel={() => {
              setShowDeleteDialog({ show: false, type: null, data: null });
            }}
            confirmVariant="delete"
            cancelVariant="secondary"
            confirmText="Delete"
            cancelText="Cancel"
            iconVariant="delete"
          />
        )}

        {showDeleteTemplateDialog && (
          <ConfirmationDialogMulti
            open={showDeleteTemplateDialog}
            onClose={() => {
              setShowDeleteTemplateDialog(false);
            }}
            title="Delete ?"
            message="This action cannot be undone."
            onConfirm={() => {
              hanldeDeleteCustomTemplate();
            }}
            onCancel={() => {
              setShowDeleteTemplateDialog(false);
            }}
            confirmVariant="delete"
            cancelVariant="secondary"
            confirmText="Delete"
            cancelText="Cancel"
            iconVariant="delete"
          />
        )}
      </div>
    </DragDropContext>
  );
};
